import styled from 'styled-components/macro'
import { useEffect, useMemo, useState } from 'react'
import ReactSelectWrapper from '../FormUtils/ReactSelectWrapper'
import FbButton from 'components/FbUI/FbButton'
import Autocomplete from 'components/FormUtils/Autocomplete'
import useCachedState from 'utils/useCachedState'
import { FilterStore, SearchFilterState } from 'components/Filters/FilterStore'
import { useAuth } from '../../context/authentication/useAuth'
import { FiSearch } from 'react-icons/fi'
import { BsChevronDown } from 'react-icons/bs'
import { PiMagnifyingGlass } from 'react-icons/pi'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { useToolbarDensity } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import { cn } from 'components/UI/cn'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
} from 'components/FbUI/ds2026DropdownMenu'

interface IElastiSearchHistory {
  [key: string]: string[]
}

interface IOption {
  label: string
  searchKey: string
  searchPlaceholder?: string
  elastic?: boolean
  /** Custom query parameter key. Defaults to searchKey for non-elastic, or 'search_query' for elastic */
  paramKey?: string
  /** Whether to include 'searchKey:' prefix in the value. Only applies when elastic=true. Defaults to true */
  includeKeyInValue?: boolean
  /** Whether to auto-set relevancy sort when this search type is applied */
  autoSetRelevancySort?: boolean
  /** Optional leading icon component (rendered inside the input on DS2026
   *  surfaces). Falls back to a magnifying glass when omitted. */
  icon?: React.ComponentType<{ size?: number; className?: string }>
}

export function TableSearchWithElastic({
  options,
  historyKey,
  filterStore,
  onSearchApplied,
}: {
  options: IOption[]
  historyKey: string
  filterStore: FilterStore
  /** Callback fired when a search is applied. Receives the option used and the search value. */
  onSearchApplied?: (option: IOption, value: string) => void
}) {
  const { user } = useAuth()
  const variant = useTableLinkVariant()
  // Both DS2026 compact tiers (sm = 32px, xs = 28px) shrink the plain search
  // shell + its inner controls so they line up with the size="sm" PillTabs /
  // Filter / Columns buttons. The exact shell height/font is driven by main.css
  // keyed off [data-search-shell][data-density='sm'|'xs'] (shared with
  // TableSearch); the inner dropdown / icon / input / submit sizes step down
  // identically for both, so a single `isCompact` flag covers them.
  const density = useToolbarDensity()
  const isCompact = density !== 'md'
  const LOCAL_STORAGE_KEY_PREFIX = `5-${user?.id}-`
  const { search, setSearch } = filterStore()

  const selectOptions = useMemo(() => {
    return options.map((option) => ({
      label: option.label,
      value: option.searchKey,
      searchKey: option.searchKey,
      searchPlaceholder: option.searchPlaceholder,
      elastic: option.elastic,
      paramKey: option.paramKey,
      includeKeyInValue: option.includeKeyInValue,
      autoSetRelevancySort: option.autoSetRelevancySort,
    }))
  }, [options])

  const initialOption =
    selectOptions?.find((option) => {
      const paramKey = option.paramKey ?? option.searchKey
      return Object.keys(search).includes(paramKey)
    }) ?? options?.[0]

  const [localSearch, setLocalSearch] = useState<SearchFilterState>(search)
  const [selectedOption, setSelectedOption] = useState<IOption>(initialOption)
  const [searchHistory, setSearchHistory] =
    useCachedState<IElastiSearchHistory>(
      {},
      LOCAL_STORAGE_KEY_PREFIX + 'search-history-' + historyKey
    )

  useEffect(() => {
    // Reset local search when elastiSearch or selectedOption changes
    setLocalSearch(search)
  }, [search, selectedOption])

  const getEffectiveParamKey = (option: IOption) => {
    // If paramKey is set, use it directly (non-elastic behavior)
    // Otherwise, use searchKey (which goes to search_query if elastic)
    return option.paramKey ?? option.searchKey
  }

  const getEffectiveElastic = (option: IOption) => {
    // If paramKey is set, treat as non-elastic to use paramKey directly
    // If includeKeyInValue is false, also treat as non-elastic
    if (option.paramKey || option.includeKeyInValue === false) {
      return false
    }
    return option.elastic ?? false
  }

  const applySearch = () => {
    const currentSearchKey = selectedOption.searchKey
    const currentParamKey = getEffectiveParamKey(selectedOption)
    const currentSearchValue = localSearch[currentSearchKey]

    // Add to search history for current key
    if (currentSearchValue?.value) {
      if (!searchHistory[currentSearchKey]) {
        searchHistory[currentSearchKey] = []
      }
      if (
        !searchHistory[currentSearchKey]
          .map((s) => s.toLowerCase())
          .includes(currentSearchValue.value.toLowerCase())
      ) {
        // Insert at the beginning
        searchHistory[currentSearchKey].unshift(currentSearchValue.value)
      }
      // Limit to last 15 searches
      if (searchHistory[currentSearchKey].length > 15) {
        searchHistory[currentSearchKey].splice(
          0,
          searchHistory[currentSearchKey].length - 15
        )
      }

      setSearchHistory((prev) => ({
        ...prev,
        [currentSearchKey]: searchHistory[currentSearchKey],
      }))
    }

    // Clear all search filters first (enforce only one active at a time)
    options.forEach((option) => {
      const optionParamKey = getEffectiveParamKey(option)
      if (optionParamKey !== currentParamKey) {
        setSearch(optionParamKey, '', '', getEffectiveElastic(option))
      }
    })

    // Apply only the current search
    if (currentSearchValue?.value) {
      setSearch(
        currentParamKey,
        currentSearchValue.value,
        currentSearchValue.label ?? selectedOption.label,
        getEffectiveElastic(selectedOption)
      )
      // Notify parent that search was applied
      onSearchApplied?.(selectedOption, currentSearchValue.value)
    }
  }

  const modeDropdown = (
    <Dropdown
      options={selectOptions}
      value={selectedOption}
      isSearchable={false}
      onChange={(option) => {
        if (option) {
          const newOption = option as IOption
          setSelectedOption(newOption)
          // Clear all other search keys from local state (only keep current)
          setLocalSearch((prev) => {
            const newSearch: SearchFilterState = {}
            if (prev[newOption.searchKey]) {
              newSearch[newOption.searchKey] = prev[newOption.searchKey]
            }
            return newSearch
          })
        }
      }}
      menuPosition="fixed"
      styles={{
        control: () => ({
          height: '100%',
          minWidth: 100,
          borderRadius: variant === 'plain' ? 6 : '6px 0 0 6px',
          cursor: 'pointer',
          borderRight: 'none',
        }),
        indicatorSeparator: () => ({
          display: 'none',
        }),
        valueContainer: (provided) => ({
          ...provided,
          padding: '2px 8px 2px 12px',
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          padding: '8px 8px 8px 0',
          color: '#343741',
          '&:hover': {
            color: '#272a31',
          },
        }),
      }}
    />
  )

  return (
    <div className="flex flex-1">
      {variant !== 'plain' && modeDropdown}
      <form
        data-search-shell={variant === 'plain' ? 'true' : undefined}
        data-density={variant === 'plain' && isCompact ? density : undefined}
        className={
          variant === 'plain'
            ? cn(
                'flex w-full items-center rounded-md border border-neutral-300 bg-white focus-within:border-neutral-400',
                isCompact ? 'h-8' : 'h-10'
              )
            : 'flex justify-between w-full relative'
        }
        onSubmit={(e) => {
          e.preventDefault()
          applySearch()
        }}
      >
        {variant === 'plain' && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-search-mode-leading
                className={cn(
                  // rounded-l matches the shell radius (6px in both densities)
                  // so the hover fill follows the shell's rounded corner instead
                  // of squaring it off.
                  'flex h-full cursor-pointer items-center justify-between gap-2 self-stretch rounded-l-md border-r border-neutral-200 font-medium text-neutral-700 outline-none hover:bg-neutral-100 focus-visible:bg-neutral-100',
                  isCompact
                    ? 'min-w-[120px] px-3 text-sm'
                    : 'min-w-[140px] px-4 text-base'
                )}
              >
                <span className="inline-flex items-center gap-2">
                  <PiMagnifyingGlass
                    data-search-icon="true"
                    size={isCompact ? 16 : 18}
                    className="shrink-0 text-neutral-500"
                  />
                  <span className="truncate">{selectedOption.label}</span>
                </span>
                <BsChevronDown className="shrink-0 text-neutral-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className={cn(ds2026DropdownContentClass, 'min-w-[140px]')}
            >
              {options.map((option) => (
                <DropdownMenuItem
                  key={option.searchKey}
                  className={ds2026DropdownItemClass}
                  onSelect={() => {
                    setSelectedOption(option)
                    setLocalSearch((prev) => {
                      const newSearch: SearchFilterState = {}
                      if (prev[option.searchKey]) {
                        newSearch[option.searchKey] = prev[option.searchKey]
                      }
                      return newSearch
                    })
                  }}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {variant === 'plain' &&
          (() => {
            const LeadingIcon = selectedOption.icon ?? FiSearch
            return (
              <LeadingIcon
                size={isCompact ? 16 : 18}
                className={cn(
                  'pointer-events-none shrink-0 text-neutral-500',
                  isCompact ? 'ml-2.5' : 'ml-3'
                )}
              />
            )
          })()}
        <Autocomplete
          type="text"
          options={searchHistory[selectedOption.searchKey] ?? []}
          value={localSearch[selectedOption.searchKey]?.value ?? ''}
          onChange={(e) =>
            setLocalSearch((prev) => ({
              ...prev,
              [selectedOption.searchKey]: {
                value: e.target.value.replaceAll(/[;:]/g, ''),
                label: selectedOption.label,
                elastic: selectedOption.elastic,
              },
            }))
          }
          placeholder={selectedOption.searchPlaceholder}
          className={
            variant === 'plain'
              ? cn(
                  'flex-1 min-w-[150px] bg-transparent border-0 outline-none px-2',
                  isCompact ? 'py-1 text-sm' : 'py-1.5 text-base'
                )
              : 'form-control flex-1 min-w-[150px]'
          }
          style={
            variant === 'plain' ? { flex: 1 } : { borderRadius: 0, flex: 1 }
          }
        />
        {variant === 'plain' &&
          !!localSearch[selectedOption.searchKey]?.value && (
            <button
              type="submit"
              data-search-submit
              className={cn(
                'inline-flex h-full items-center self-stretch rounded-l-none rounded-r-md bg-brand-bright font-medium text-white transition-colors hover:bg-brand-active',
                isCompact ? 'px-3 text-sm' : 'px-4 text-base'
              )}
            >
              Search
            </button>
          )}
        {variant !== 'plain' && (
          <FbButton
            className="-ml-1 h-full font-semibold rounded-tr-lg rounded-br-lg rounded-tl-none rounded-bl-none"
            onClick={applySearch}
          >
            <FiSearch size={19} />
          </FbButton>
        )}
      </form>
    </div>
  )
}

export const SearchButton = styled(FbButton)`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  font-size: 1rem;
  line-height: normal;
  border-radius: 0 6px 6px 0;
  font-weight: 600 !important;
`

export const Dropdown = styled(ReactSelectWrapper)`
  & > .form-control-select {
    background: #eef0f1;
    color: #343741 !important;
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 600;
  }
`
